import React, { useState, useEffect, useRef } from "react";
import {
  Menu,
  ChevronDown,
  User,
  LogOut,
  Settings,
  RefreshCw
} from "lucide-react";
import { useLocation } from "react-router-dom";

const Header = ({ toggleSidebar }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userName, setUserName] = useState("User");
  const [userInitial, setUserInitial] = useState("U");
  const [pageTitle, setPageTitle] = useState("Dashboard");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const dropdownRef = useRef(null);

  const location = useLocation();

  // Update page title to BluTec PING naming
  useEffect(() => {
    const path = location.pathname;
    if (path.includes("dashboard")) setPageTitle("Messaging Analytics");
    else if (path.includes("main")) setPageTitle("Instances");
    else if (path.includes("docs")) setPageTitle("API Documentation");
    else if (path.includes("profile")) setPageTitle("Your Profile");
    else setPageTitle("Messaging Dashboard");
  }, [location]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        if (parsedUser.name) {
          setUserName(parsedUser.name);
          setUserInitial(parsedUser.name.charAt(0).toUpperCase());
        }
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
  }, []);

  const toggleUserMenu = () => setShowUserMenu((v) => !v);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userAccessToken");
    localStorage.removeItem("user_id");
    window.location.href = "/login";
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.location.reload();
    setTimeout(() => setIsRefreshing(false), 2000);
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showUserMenu]);

  return (
    <header className="fixed top-0 right-0 left-0 lg:left-64 z-50 h-14">
      <div className="h-full w-full border-b border-gray-200 bg-white shadow-sm">
        <div className="max-w-full mx-auto h-full px-3 flex items-center justify-between">
          {/* Left: menu + title */}
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-1.5 rounded-lg text-gray-600 hover:text-primary hover:bg-gray-50 ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
              aria-label="Toggle sidebar"
            >
              <Menu className="w-4 h-4" />
            </button>

            <div className="min-w-0">
              <h1 className="text-base md:text-lg font-extrabold tracking-tight truncate text-gray-900">
                <span className="text-primary">
                  {pageTitle}
                </span>
              </h1>
              <p className="hidden sm:block text-xs text-gray-500">
                {/* Optional subtitle */}
              </p>
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1.5">
            {/* Refresh */}
            <button
              onClick={handleRefresh}
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs text-primary bg-gray-50 hover:bg-gray-100 ring-1 ring-gray-200 transition focus:outline-none focus:ring-2 focus:ring-primary/40"
              aria-label="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Refresh</span>
            </button>

            {/* User menu */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleUserMenu}
                className="flex items-center gap-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 px-2 py-1.5 ring-1 ring-gray-200 transition focus:outline-none focus:ring-2 focus:ring-primary/40"
                aria-haspopup="menu"
                aria-expanded={showUserMenu}
              >
                <div className="w-6 h-6 rounded-full bg-primary text-white font-semibold flex items-center justify-center shadow-sm text-xs">
                  {userInitial}
                </div>
                <span className="hidden sm:block text-xs text-gray-700 truncate max-w-24 md:max-w-[140px]">
                  {userName}
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
              </button>

              {showUserMenu && (
                <div
                  role="menu"
                  className="absolute right-0 mt-1.5 w-44 rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden"
                >
                  <div className="px-3 py-2 border-b border-gray-200">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {userName}
                    </p>
                    <p className="text-xs text-gray-500">User</p>
                  </div>
                  <ul className="py-0.5">
                    <li>
                      <a
                        href="/profile"
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition"
                        role="menuitem"
                      >
                        <User className="w-3.5 h-3.5" />
                        Your Profile
                      </a>
                    </li>
                    <li>
                      <a
                        href="/settings"
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition"
                        role="menuitem"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        Settings
                      </a>
                    </li>
                    <li className="border-t border-gray-200">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-gray-50 hover:text-red-700 transition"
                        role="menuitem"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Sign Out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
